(ns app.front.ui.contact-request-panel
  (:require [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.panels :as panels]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.side-panel :as side-panel]
            [app.front.util :as futil]
            [core.domain.validators :as validators]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.media-query :as media-query]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-db ::save-attr
  (fn [db [_ attr value]]
    (assoc-in db [::contact-request-data attr] value)))

(defn- mobile? [db]
  (-> db ::media-query/currently-matching-breakpoints (contains? :lg) not))

(defn- panel-open? [db]
  (-> db :ui/open-panel first (= :contact-request)))

(rf/reg-event-db ::cleanup
  (fn [db _]
    (dissoc db
            ::contact-request-data
            ::domain-group-id
            ::municipal-case-id)))

(rf/reg-event-fx ::toggle-open
  (fn [{:keys [db]} [_ {:keys [domain-group-id municipal-case-id]}]]
    (let [already-open? (panel-open? db)]
      (merge {:fx (concat [[:dispatch [::panels/toggle :contact-request]]]
                          (when (mobile? db)
                            [[:dispatch [::panels/toggle-mobile-menu-open (not already-open?)]]]))}
             (if already-open?
               {:dispatch [::cleanup]}
               {:db (assoc db
                           ::domain-group-id domain-group-id
                           ::municipal-case-id municipal-case-id)})))))

(rf/reg-event-fx ::close
  (fn [{:keys [db]} _]
    (let [open? (panel-open? db)]
      (when open?
        {:fx (concat [[:dispatch [::panels/toggle :contact-request]]
                      [:dispatch [::cleanup]]]
                     (when (mobile? db)
                       [[:dispatch [::panels/toggle-mobile-menu-open false]]]))}))))

(rf/reg-sub ::panel-open?
  :<- [::panels/open? :contact-request]
  :-> boolean)

(rf/reg-sub ::domain-group-id
  :-> ::domain-group-id)

(rf/reg-sub ::municipal-case-id
  :-> ::municipal-case-id)

(rf/reg-sub ::contact-request-data
  (fn [db [_ attr]]
    (get-in db (remove nil? [::contact-request-data attr]))))

(rf/reg-event-fx ::send-contact-request
  (fn [{:keys [db]} [_ domain-group-id municipal-case-id]]
    {:comm/command {:command    :public-map/contact-request
                    :data       (p/assoc-if (::contact-request-data db)
                                            :domain-group-id domain-group-id
                                            :municipal-case-id municipal-case-id)
                    :method     :post
                    :notify     {:success :public-map-view/contact-request-submitted}
                    :on-success [::close]}}))

(rf/reg-sub ::save-disabled?
  :<- [:comm/command-processing? :public-map/contact-request]
  :<- [::contact-request-data]
  (fn [[sending-contact-request? contact-request-data]]
    (or sending-contact-request?
        (not (and (:contact-request/details contact-request-data)
                  (:contact-request/fullname contact-request-data)
                  (:contact-request/contact-by contact-request-data)
                  (:contact-request/phone contact-request-data)
                  (validators/valid-email? (:contact-request/email contact-request-data)))))))

(defn contact-request-form [tr]
  (let [domain-group-id   (<sub [::domain-group-id])
        municipal-case-id (<sub [::municipal-case-id])]
    [:div
     [ui/text-area {:data-cy     "contact-request-content"
                    :label-text  (tr [:public-map-view/contact-request-details])
                    :input-class "h-full"
                    :required    true
                    :value       (<sub [::contact-request-data :contact-request/details])
                    :on-change   #(>evt [::save-attr :contact-request/details (futil/tval %)])}]
     [:div.mt-5.flex.flex-col.gap-5
      [:div.flex-1
       [ui/text-input
        {:id         "contact-request-name"
         :value      (<sub [::contact-request-data :contact-request/fullname])
         :label-text (tr [:public-map-view/contact-request-name])
         :required   true
         :on-change  #(>evt [::save-attr :contact-request/fullname (futil/tval %)])}]]
      [:div.flex-1
       [ui/text-input
        {:id         "contact-request-email"
         :value      (<sub [::contact-request-data :contact-request/email])
         :label-text (tr [:public-map-view/contact-request-email])
         :required   true
         :on-change  #(>evt [::save-attr :contact-request/email (futil/tval %)])}]]]
     [:div.mt-5.flex.flex-col.gap-5
      [:div.flex-1
       [ui/intl-tel-input {:id              "contact-request-phone"
                           :type            :text
                           :label-text      (tr [:public-map-view/contact-request-phone])
                           :placeholder     (tr [:placeholder/international-phone])
                           :required        true
                           :value           (<sub [::contact-request-data :contact-request/phone])
                           :callback        #(rf/dispatch-sync [::save-attr :contact-request/phone %])
                           :initial-country (<sub [::front-config/country])}]]
      [:div.flex-1
       [ui-radio/radio {:name       "contact-me-by"
                        :label-text (tr [:public-map-view/contact-request-contact-by])
                        :values     [:email :phone]
                        :required   true
                        :value      (<sub [::contact-request-data :contact-request/contact-by])
                        :val->str   (comp tr vector (partial keyword "public-map-view"))
                        :on-change  #(>evt [::save-attr :contact-request/contact-by %])}]]]
     [:div.mt-5
      [ui-action-buttons/save-cancel-buttons
       {:on-cancel      #(>evt [::close])
        :on-save        #(>evt [::send-contact-request domain-group-id municipal-case-id])
        :save-tr        (tr [:public-map-view/submit])
        :save-disabled? (<sub [::save-disabled?])
        :size           :small
        :tr             tr}]]]))

(defn contact-request-panel [tr]
  (let [panel-open? (<sub [::panel-open?])]
    [side-panel/side-panel {:panel-open? panel-open? :panel-id :contact-request}
     [side-panel/title (tr [:public-map-view/contact-request])]
     [contact-request-form tr]]))
